import React, { useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { COLORS } from "components/v3/Theme/colors";
import Translate from "translate/Translate";
import GLButton from "components/v3/sharedComponents/GLButton";
import ModalWrapper from "components/v2/sharedComponents/Modals/ModalComponents/ModalWrapper";
import SuggestedScreeningCard, { type Kit } from "components/v3/sharedComponents/SuggestedScreeningCard";
import Mixpanel from "utils/mixpanel";

type SuggestedScreeningsConfirmationModalProps = {
  existingTestKitOrders?: Kit[];
  isOpen?: boolean;
  kitOffers?: Kit[];
  kitsToOfferMemberRightNow?: Kit[];
  numTestsAvailable?: number;
  onCloseModal?: () => void;
  onSubmit?: (params: { add: boolean }) => void;
  selectedKitUuids?: string[];
};

const SuggestedScreeningsConfirmationModal: React.FC<React.PropsWithChildren<
  SuggestedScreeningsConfirmationModalProps
>> = ({
  existingTestKitOrders,
  isOpen = false,
  kitOffers,
  kitsToOfferMemberRightNow,
  onCloseModal,
  onSubmit,
  selectedKitUuids,
}) => {
  const suggestedKitOffers = kitOffers?.filter((kit) => {
    const kitIsSuggested = !!kitsToOfferMemberRightNow.find(
      (suggestedKit) => suggestedKit.test_uuid === kit.test_uuid
    );
    
    return kitIsSuggested;
  });
  const suggestedKitOffersNotSelected = suggestedKitOffers?.filter((suggestedKit) => {
    const kitIsSelected = !!selectedKitUuids.includes(suggestedKit.test_uuid);
    
    return !kitIsSelected;
  });
  const numSuggestedKitOffersNotSelected = suggestedKitOffersNotSelected?.length;
  const hasSuggestedKitOffersNotSelected = numSuggestedKitOffersNotSelected > 0;
  const hasMultipleSuggestedKitOffersNotSelected = numSuggestedKitOffersNotSelected > 1;

  useEffect(() => {
    if (isOpen && hasSuggestedKitOffersNotSelected) {
      Mixpanel.track("Test selection confirmation modal displayed", {
        tests: suggestedKitOffersNotSelected.map((kit) => kit?.glTestType).join(',')
      });
    }
  }, [hasSuggestedKitOffersNotSelected, isOpen, suggestedKitOffersNotSelected]);

  if (!hasSuggestedKitOffersNotSelected) {
    return null;
  }

  return (
    <ModalWrapper
      closeOnBackdropClick={false}
      isOpen={isOpen}
      onClose={onCloseModal}
      modalContainerSx={{
        flexBasis: {
          xs: "90% !important"
        },
        maxHeight: "85vh",
        maxWidth: "820px !important",
        overflow: "auto",
        padding: {
          xs: "32px 16px 24px 16px !important",
          lg: "64px 32px 32px 32px !important"
        },
      }}
      sx={{
        "& .MuiModal-backdrop": {
          backgroundColor: `${COLORS.PRIMARY_DARKER}80`
        }
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
          gap: 4,
          textAlign: "center"
        }}
      >
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            maxWidth: {
              md: "75%"
            }
          }}
        >
          <Typography sx={{ typography: { xs: "body_strong", lg: "body_strong_lg" } }}>
            <Translate text={"Let's make sure you have everything"} />
          </Typography>
          <Typography sx={{ typography: { xs: "body_xs", xsm: "body" } }}>
            <Translate
              text={
                hasMultipleSuggestedKitOffersNotSelected
                  ? "You still have these tests available, would you like to add them to your request too? No rush, you can always request them later."
                  : "You still have this test available, would you like to add them to your request too? No rush, you can always request them later."
              }
            />
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: {
              xs: "column",
              lg: "row"
            },
            gap: 2,
          }}
        >
          {suggestedKitOffersNotSelected.map((kit, index) => {
            return (
              <Box
                key={`suggested-screening-card-${index}-${kit.test_uuid.slice(0, 2)}`}
                sx={{ userSelect: "none" }}
              >
                <SuggestedScreeningCard
                  displayOnly={true}
                  existingTestKitOrders={existingTestKitOrders}
                  kit={kit}
                  suggested={true}
                  userHasOrder={existingTestKitOrders.length > 0}
                />
              </Box>
            );
          })}
        </Box>
        <GLButton
          buttonType="primary"
          onClick={() => {
            onSubmit?.({ add: true });
          }}
        >
          <Typography variant="body_strong_white">
            <Translate
              text={hasMultipleSuggestedKitOffersNotSelected ? "Add both to request" : "Add to request"}
            />
          </Typography>
        </GLButton>
        <Box
          component="button"
          onClick={() => {
            onSubmit?.({ add: false });
          }}
          sx={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
          }}
        >
          <Typography variant="body_strong_dark_gray">
            <Translate text="No thanks, continue with request" />
          </Typography>
        </Box>
      </Box>
    </ModalWrapper>
  );
};

export default SuggestedScreeningsConfirmationModal;
